package theme

data class ThemeColors(
    val background: String,
    val card: String,
    val border: String,
    val textPrimary: String,
    val textSecondary: String,
    val accent: String,
    val accentStrong: String,
    val incomeGreen: String,
    val expenseRed: String,
    val targetOrange: String,
    val proBadge: String,
    val proBadgeText: String,
    val chipSelectedBg: String,
    val chipSelectedBorder: String,
    val chipUnselectedBg: String,
    val chipUnselectedBorder: String,
    val chipUnselectedText: String,
    val navBackground: String,
    val navText: String,
    val navActive: String,
    val iconCircleDefault: String,
    val iconStroke: String,
    val cardShadow: String,
    val isDark: Boolean,
)

val DarkTheme = ThemeColors(
    background = "#0A0A0A",
    card = "#141416",
    border = "#2A2A2E",
    textPrimary = "#FFFFFF",
    textSecondary = "#ABABB8",
    accent = "#00D9FF",
    accentStrong = "#00D9FF",
    incomeGreen = "#32CD32",
    expenseRed = "#FF6B6B",
    targetOrange = "#FFA600",
    proBadge = "#F59E0B",
    proBadgeText = "#000000",
    chipSelectedBg = "rgba(0,217,255,0.15)",
    chipSelectedBorder = "#00D9FF",
    chipUnselectedBg = "#1E1E22",
    chipUnselectedBorder = "#444444",
    chipUnselectedText = "#999999",
    navBackground = "#0A0A0A",  // fully opaque — used to be semi-transparent
    // (paired with backdrop-filter blur for frosted-glass), but the blur was
    // forcing the browser to re-blur the entire viewport behind the nav on
    // every animation frame. The blur is now removed (see app nav bars) and
    // the bg is solid instead — same visual presence, none of the GPU cost.
    navText = "#888888",
    navActive = "#00D9FF",
    iconCircleDefault = "#2C2C2E",
    iconStroke = "#FFFFFF",
    cardShadow = "none",
    isDark = true,
)

val LightTheme = ThemeColors(
    background = "#E8E2D6",       // warm beige — page background
    card = "#F5F2EC",             // cream — card surfaces, nav bar
    border = "#D4CEBE",           // warm gray-brown — borders, dividers
    textPrimary = "#3A3632",      // warm charcoal — softer than pure black
    textSecondary = "#7A756D",    // warm gray — better contrast on beige
    accent = "#0097A7",
    accentStrong = "#00838F",
    incomeGreen = "#1B8A3C",
    expenseRed = "#C0392B",
    targetOrange = "#E67E00",
    proBadge = "#D97706",
    proBadgeText = "#FFFFFF",
    chipSelectedBg = "rgba(0,151,167,0.12)",
    chipSelectedBorder = "#0097A7",
    chipUnselectedBg = "#EAE6DF",
    chipUnselectedBorder = "#C8C2B8",
    chipUnselectedText = "#86868B",
    navBackground = "#F5F2EC",    // same as card
    navText = "#86868B",
    navActive = "#00838F",
    iconCircleDefault = "#E8E2D6",
    iconStroke = "#FFFFFF",
    cardShadow = "0 1px 4px rgba(0,0,0,0.07)",
    isDark = false,
)

/**
 * For light mode: darken a category color so white icons remain legible.
 * Clamps HSL lightness to max 40%.
 */
fun darkenForLightTheme(hexColor: String): String {
    val hex = hexColor.trimStart('#')
    if (hex.length != 6) return hexColor

    val r = hex.substring(0, 2).toInt(16) / 255.0
    val g = hex.substring(2, 4).toInt(16) / 255.0
    val b = hex.substring(4, 6).toInt(16) / 255.0

    val max = maxOf(r, g, b)
    val min = minOf(r, g, b)
    val l = (max + min) / 2.0

    if (l <= 0.40) return hexColor

    val d = max - min
    val s = if (d == 0.0) 0.0 else if (l > 0.5) d / (2.0 - max - min) else d / (max + min)
    val h = when {
        d == 0.0 -> 0.0
        max == r -> ((g - b) / d + (if (g < b) 6 else 0)) / 6.0
        max == g -> ((b - r) / d + 2) / 6.0
        else -> ((r - g) / d + 4) / 6.0
    }

    val newL = 0.40

    fun hue2rgb(p: Double, q: Double, t: Double): Double {
        val tt = if (t < 0) t + 1 else if (t > 1) t - 1 else t
        return when {
            tt < 1.0 / 6 -> p + (q - p) * 6 * tt
            tt < 1.0 / 2 -> q
            tt < 2.0 / 3 -> p + (q - p) * (2.0 / 3 - tt) * 6
            else -> p
        }
    }

    val q = if (newL < 0.5) newL * (1 + s) else newL + s - newL * s
    val p = 2 * newL - q
    val nr = (hue2rgb(p, q, h + 1.0 / 3) * 255).toInt().coerceIn(0, 255)
    val ng = (hue2rgb(p, q, h) * 255).toInt().coerceIn(0, 255)
    val nb = (hue2rgb(p, q, h - 1.0 / 3) * 255).toInt().coerceIn(0, 255)

    return "#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}".uppercase()
}

fun getIconCircleFill(categoryColor: String, isDark: Boolean): String {
    return if (isDark) "#2C2C2E" else darkenForLightTheme(categoryColor)
}

fun getIconCircleBorderVisible(isDark: Boolean): Boolean = isDark

object ThemePreference {
    const val SYSTEM = "system"
    const val DARK = "dark"
    const val LIGHT = "light"
}
